import React, { useEffect, useState, useCallback } from 'react';
import Styles from '../assets/css/productDetail.module.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { updateProductQuantity as updateQuantityAPI, productTransaction, getProductDetailsById, getProductByBarcodeNumber } from '../services/Api';
import { useDispatch, useSelector } from 'react-redux';
import { popUpBoxTrigger } from "../features/message/popUpSlice";
import PopUpMessage from "../utils/errors/PopUpMessage";
import Loader from '../utils/Loader';
import { setProductsForTransaction, addProductToTransaction, removeProductFromTransaction } from '../features/scanproduct/scanProduct';
import ProductDetailCard from './ProductDetailCard';
import QuantityManager from './QuantityManager';
import TransactionTable from './TransactionTable';

const ProductDetail = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [productLoading, setProductLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const { transactionId } = useParams();
    const [error, setError] = useState(null);
    const [productData, setProductData] = useState(null);
    const [productsData, setProductsData] = useState([]);
    const [transactionProducts, setTransactionProducts] = useState([]);
    const [productTransactionDataWithQuantity, setProductTransactionDataWithQuantity] = useState([]);
    const [isOutbound, setIsOutbound] = useState(location.state?.isOutbound || false);
    const [maxQuantity, setMaxQuantity] = useState(0);
    const [contentReady, setContentReady] = useState(false);
    const transactions = useSelector(state => state.scanProducts.transactions);
    const [productId, setProductId] = useState();
    const [isInitialized, setIsInitialized] = useState(false);
    const [originalQuantities, setOriginalQuantities] = useState({});
    const [newlyCreatedProducts, setNewlyCreatedProducts] = useState(new Set());
    const [selectedSku, setSelectedSku] = useState(null);
    const [skuQuantities, setSkuQuantities] = useState({});

    // Get available quantity for a specific SKU
    const getAvailableQuantityForSku = (sku) => {
        if (!sku) return 0;
        return sku.quantity || 0;
    };

    // Get total available quantity across all SKUs
    const getTotalAvailableQuantity = (product) => {
        if (!product || !product.lazada_product?.data?.skus) return 0;

        return product.lazada_product.data.skus.reduce((total, sku) => {
            return total + (sku.quantity || 0);
        }, 0);
    };

    const showPopup = useCallback((type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    }, [dispatch]);

    // Initialize SKU selection and quantities with ACTUAL SKU QUANTITIES
    useEffect(() => {
        if (productData && productData.lazada_product?.data?.skus) {
            const skus = productData.lazada_product.data.skus;

            // Check if we already have transaction data for this product
            const existingTransactionItem = productTransactionDataWithQuantity.find(
                item => item.id === productData.id
            );

            if (existingTransactionItem && existingTransactionItem.skuQuantities) {
                // Use existing SKU quantities
                setSkuQuantities(existingTransactionItem.skuQuantities);

                // Find selected SKU from existing data (first one with quantity > 0)
                const firstSkuWithQty = skus.find(sku =>
                    existingTransactionItem.skuQuantities[sku.SkuId] > 0
                );

                if (firstSkuWithQty) {
                    const selectedSkuObj = {
                        SkuId: firstSkuWithQty.SkuId,
                        SellerSku: firstSkuWithQty.SellerSku,
                        quantity: firstSkuWithQty.quantity || 0,
                        variation: firstSkuWithQty.saleProp || {}
                    };
                    setSelectedSku(selectedSkuObj);
                    setMaxQuantity(firstSkuWithQty.quantity || 0);
                }
            } else {
                // Initialize fresh
                if (skus.length > 0 && !selectedSku) {
                    const firstSku = skus[0];
                    setSelectedSku({
                        SkuId: firstSku.SkuId,
                        SellerSku: firstSku.SellerSku,
                        quantity: firstSku.quantity || 0,
                        variation: firstSku.saleProp || {}
                    });

                    // Set max quantity for the selected SKU
                    setMaxQuantity(firstSku.quantity || 0);
                }

                // Initialize SKU quantities
                const initialSkuQuantities = {};
                skus.forEach(sku => {
                    if (isOutbound) {
                        initialSkuQuantities[sku.SkuId] = 1;
                    } else {
                        initialSkuQuantities[sku.SkuId] = sku.quantity || 0;
                    }
                });
                setSkuQuantities(initialSkuQuantities);

                // Update transaction data
                const totalQuantity = Object.values(initialSkuQuantities).reduce((sum, qty) => sum + qty, 0);

                setProductTransactionDataWithQuantity(prevData => {
                    const existingItemIndex = prevData.findIndex(item => item.id === productData.id);
                    if (existingItemIndex !== -1) {
                        const updatedData = [...prevData];
                        updatedData[existingItemIndex] = {
                            ...updatedData[existingItemIndex],
                            quantity: totalQuantity,
                            skuQuantities: initialSkuQuantities
                        };
                        return updatedData;
                    } else {
                        return [...prevData, {
                            id: productData.id,
                            quantity: totalQuantity,
                            skuQuantities: initialSkuQuantities
                        }];
                    }
                });
            }
        }
    }, [productData]);

    useEffect(() => {
        if (isInitialized) return;
        const initializeFromState = async () => {
            try {
                setProductLoading(true);

                if (location.state?.existingProducts) {
                    const products = location.state.existingProducts;
                    const originalQtys = {};
                    const updatedProducts = [];
                    const processedProductIds = new Set();

                    for (const product of products) {
                        if (processedProductIds.has(product.id)) {
                            continue;
                        }

                        processedProductIds.add(product.id);

                        try {
                            const response = await getProductDetailsById(product.id);
                            if (response.status === 200) {
                                const availableQty = getTotalAvailableQuantity(response.data);
                                originalQtys[product.id] = availableQty;

                                // Find transaction data for this product
                                const transactionItem = productTransactionDataWithQuantity.find(
                                    item => item.id === product.id
                                );

                                updatedProducts.push({
                                    ...response.data,
                                    quantity: transactionItem?.quantity || (isOutbound ? 1 : availableQty),
                                    originalDbQuantity: availableQty
                                });
                            }
                        } catch (error) {
                            console.error('Error fetching product details for', product.id, error);
                            originalQtys[product.id] = 0;
                            updatedProducts.push({
                                ...product,
                                quantity: isOutbound ? 1 : 0
                            });
                        }
                    }

                    setProductsData(updatedProducts);
                    setOriginalQuantities(originalQtys);

                    // Initialize transaction data
                    const uniqueTransactionProducts = [];
                    const processedTransactionIds = new Set();

                    for (const product of updatedProducts) {
                        if (processedTransactionIds.has(product.id)) {
                            continue;
                        }
                        processedTransactionIds.add(product.id);

                        // Get SKU quantities - check if we already have them from previous operations
                        const existingTransactionItem = productTransactionDataWithQuantity.find(
                            item => item.id === product.id
                        );

                        let skuQuantitiesForProduct = {};

                        if (existingTransactionItem && existingTransactionItem.skuQuantities) {
                            // Use existing SKU quantities
                            skuQuantitiesForProduct = { ...existingTransactionItem.skuQuantities };
                        } else {
                            // Initialize fresh
                            const skus = product.lazada_product?.data?.skus || [];
                            skus.forEach(sku => {
                                if (isOutbound) {
                                    skuQuantitiesForProduct[sku.SkuId] = 1;
                                } else {
                                    skuQuantitiesForProduct[sku.SkuId] = sku.quantity || 0;
                                }
                            });
                        }

                        uniqueTransactionProducts.push({
                            id: product.id,
                            quantity: product.quantity,
                            skuQuantities: skuQuantitiesForProduct
                        });
                    }

                    setProductTransactionDataWithQuantity(uniqueTransactionProducts);

                    const targetProductId = location.state?.selectedProductId || updatedProducts[0]?.id;
                    if (targetProductId) {
                        await getProductDetails(targetProductId);
                    }

                    setIsInitialized(true);
                }
                setContentReady(true);
            } catch (error) {
                console.error('Error initializing from state:', error);
                showPopup('error', 'Error', 'Failed to load product details');
            } finally {
                setProductLoading(false);
            }
        };

        initializeFromState();
    }, [location.state, isOutbound, isInitialized, showPopup]);

    // Handle SKU selection change
    const handleSkuChange = (sku) => {
        setSelectedSku(sku);
        // Update max quantity for the selected SKU
        const availableQty = getAvailableQuantityForSku(sku);
        setMaxQuantity(availableQty);
    };

    // Get current quantity for selected SKU
    const getCurrentSkuQuantity = () => {
        if (!selectedSku) return 0;
        return skuQuantities[selectedSku.SkuId] || 0;
    };

    // Handle quantity changes for specific SKU
    const handleSkuQuantityChange = (skuId, newQuantity) => {
        setSkuQuantities(prev => ({
            ...prev,
            [skuId]: newQuantity
        }));

        // Also update the main product transaction data with total quantity
        if (productData) {
            const totalQuantity = Object.values({ ...skuQuantities, [skuId]: newQuantity })
                .reduce((sum, qty) => sum + qty, 0);

            setProductTransactionDataWithQuantity(prevData => {
                const existingItemIndex = prevData.findIndex(item => item.id === productData.id);
                if (existingItemIndex !== -1) {
                    const updatedData = [...prevData];
                    updatedData[existingItemIndex] = {
                        ...updatedData[existingItemIndex],
                        quantity: totalQuantity,
                        skuQuantities: { ...skuQuantities, [skuId]: newQuantity }
                    };
                    return updatedData;
                } else {
                    return [...prevData, {
                        id: productData.id,
                        quantity: totalQuantity,
                        skuQuantities: { ...skuQuantities, [skuId]: newQuantity }
                    }];
                }
            });

            setProductsData(prevData => {
                const existingItemIndex = prevData.findIndex(item => item.id === productData.id);
                if (existingItemIndex !== -1) {
                    const updatedData = [...prevData];
                    updatedData[existingItemIndex] = {
                        ...updatedData[existingItemIndex],
                        quantity: totalQuantity
                    };
                    return updatedData;
                } else {
                    return [...prevData, {
                        id: productData.id,
                        product_name: productData.product_name,
                        sku_number: productData.sku_number,
                        quantity: totalQuantity,
                        lazada_product: productData.lazada_product
                    }];
                }
            });
        }
    };

    // Handle product selection from transaction table
    const handleProductSelect = async (productId) => {
        try {
            setProductLoading(true);

            // Find existing transaction data for this product FIRST
            const existingTransactionItem = productTransactionDataWithQuantity.find(
                item => item.id === productId
            );

            // Fetch fresh product details
            const response = await getProductDetailsById(productId);
            if (response.status === 200) {
                const productDetails = response.data;
                const skus = productDetails.lazada_product?.data?.skus || [];

                // Initialize SKU quantities - Check for saved data first
                let initialSkuQuantities = {};
                let selectedSkuObj = null;

                if (existingTransactionItem && existingTransactionItem.skuQuantities) {
                    // Use saved SKU quantities from transaction data
                    initialSkuQuantities = { ...existingTransactionItem.skuQuantities };

                    // Find the first SKU that has quantity > 0, or use the first SKU
                    const firstSkuWithQuantity = skus.find(sku =>
                        initialSkuQuantities[sku.SkuId] && initialSkuQuantities[sku.SkuId] > 0
                    );

                    selectedSkuObj = firstSkuWithQuantity ? {
                        SkuId: firstSkuWithQuantity.SkuId,
                        SellerSku: firstSkuWithQuantity.SellerSku,
                        quantity: firstSkuWithQuantity.quantity || 0,
                        variation: firstSkuWithQuantity.saleProp || {}
                    } : (skus.length > 0 ? {
                        SkuId: skus[0].SkuId,
                        SellerSku: skus[0].SellerSku,
                        quantity: skus[0].quantity || 0,
                        variation: skus[0].saleProp || {}
                    } : null);
                } else {
                    // No saved data, initialize fresh
                    skus.forEach(sku => {
                        if (isOutbound) {
                            initialSkuQuantities[sku.SkuId] = 1;
                        } else {
                            initialSkuQuantities[sku.SkuId] = sku.quantity || 0;
                        }
                    });

                    // Set selected SKU (default to first)
                    if (skus.length > 0) {
                        selectedSkuObj = {
                            SkuId: skus[0].SkuId,
                            SellerSku: skus[0].SellerSku,
                            quantity: skus[0].quantity || 0,
                            variation: skus[0].saleProp || {}
                        };
                    }
                }

                // Set states
                setSkuQuantities(initialSkuQuantities);
                setSelectedSku(selectedSkuObj);

                if (selectedSkuObj) {
                    // Set max quantity for the selected SKU
                    const availableQty = getAvailableQuantityForSku(selectedSkuObj);
                    setMaxQuantity(availableQty);
                }

                // Calculate total quantity for this product
                const totalQuantity = Object.values(initialSkuQuantities).reduce((sum, qty) => sum + qty, 0);
                const availableQty = getTotalAvailableQuantity(productDetails);

                // Update product data
                setProductData({
                    ...productDetails,
                    quantity: totalQuantity,
                    originalDbQuantity: availableQty,
                });
            }
        } catch (error) {
            console.error('Error loading selected product:', error);
            showPopup('error', 'Error', 'Failed to load product details');
        } finally {
            setProductLoading(false);
        }
    };

    const handleIncrease = (id) => {
        if (!selectedSku) return;

        if (isOutbound) {
            const currentSkuQty = getCurrentSkuQuantity();
            const availableQty = getAvailableQuantityForSku(selectedSku);

            if (currentSkuQty >= availableQty) {
                showPopup('error', 'Quantity Limit', `You can only outbound up to ${availableQty} units of this SKU.`);
                return;
            }
        }

        const newQuantity = getCurrentSkuQuantity() + 1;
        handleSkuQuantityChange(selectedSku.SkuId, newQuantity);
        dispatch(addProductToTransaction({ transactionId, productId: id }));
    };

    const handleDecrease = (id) => {
        if (!selectedSku) return;

        const currentSkuQty = getCurrentSkuQuantity();
        const availableQty = getAvailableQuantityForSku(selectedSku);

        if (isOutbound) {
            if (currentSkuQty > 1) {
                const newQuantity = currentSkuQty - 1;
                handleSkuQuantityChange(selectedSku.SkuId, newQuantity);
                dispatch(removeProductFromTransaction({ transactionId, productId: id }));
            } else {
                showPopup('error', 'Quantity Limit', 'Outbound quantity cannot be less than 1.');
            }
        } else {
            if (currentSkuQty > availableQty) {
                const newQuantity = currentSkuQty - 1;
                handleSkuQuantityChange(selectedSku.SkuId, newQuantity);
                dispatch(removeProductFromTransaction({ transactionId, productId: id }));
            } else {
                showPopup('error', 'Quantity Limit', `Inbound quantity cannot be less than available quantity (${availableQty}).`);
            }
        }
    };

    const checkIfProductExists = (productId) => {
        return productTransactionDataWithQuantity.some(item => item.id === productId);
    };

    const findProductIndex = (productId) => {
        return productTransactionDataWithQuantity.findIndex(item => item.id === productId);
    };

    const handleAddMore = () => {
        navigate(`/scanBarcode/${transactionId || 'new'}`, {
            state: {
                existingProducts: productsData,
                existingProductIds: productsData.map(p => p.id),
                scanType: isOutbound ? "outbound" : "inbound",
                autoOpenScanner: true,
                isOutbound: isOutbound,
                // Pass current products to check duplicates
                currentProductIds: productTransactionDataWithQuantity.map(p => p.id)
            }
        });
    };

    const getProductDetails = async (id) => {
        try {
            setProductLoading(true);
            const response = await getProductDetailsById(id);
            if (response.status === 200) {
                // Find existing transaction data for this product
                const existingTransactionItem = productTransactionDataWithQuantity.find(
                    item => item.id === id
                );

                const skus = response.data.lazada_product?.data?.skus || [];
                let initialSkuQuantities = {};
                let selectedSkuData = null;

                if (existingTransactionItem && existingTransactionItem.skuQuantities) {
                    // Use saved SKU quantities
                    initialSkuQuantities = { ...existingTransactionItem.skuQuantities };

                    // Find first SKU with quantity > 0, or use first SKU
                    const firstSkuWithQuantity = skus.find(sku =>
                        initialSkuQuantities[sku.SkuId] && initialSkuQuantities[sku.SkuId] > 0
                    );

                    selectedSkuData = firstSkuWithQuantity ? {
                        SkuId: firstSkuWithQuantity.SkuId,
                        SellerSku: firstSkuWithQuantity.SellerSku,
                        quantity: firstSkuWithQuantity.quantity || 0,
                        variation: firstSkuWithQuantity.saleProp || {}
                    } : (skus.length > 0 ? {
                        SkuId: skus[0].SkuId,
                        SellerSku: skus[0].SellerSku,
                        quantity: skus[0].quantity || 0,
                        variation: skus[0].saleProp || {}
                    } : null);
                } else {
                    // No saved data, initialize fresh
                    skus.forEach(sku => {
                        if (isOutbound) {
                            initialSkuQuantities[sku.SkuId] = 1;
                        } else {
                            initialSkuQuantities[sku.SkuId] = sku.quantity || 0;
                        }
                    });

                    // Set selected SKU
                    if (skus.length > 0) {
                        selectedSkuData = {
                            SkuId: skus[0].SkuId,
                            SellerSku: skus[0].SellerSku,
                            quantity: skus[0].quantity || 0,
                            variation: skus[0].saleProp || {}
                        };
                    }
                }

                // Set SKU quantities state
                setSkuQuantities(initialSkuQuantities);
                setSelectedSku(selectedSkuData);

                if (selectedSkuData) {
                    const selectedSkuQty = initialSkuQuantities[selectedSkuData.SkuId] || 0;
                    setMaxQuantity(selectedSkuData.quantity || 0);
                }

                // Calculate total quantity
                const totalQuantity = Object.values(initialSkuQuantities).reduce((sum, qty) => sum + qty, 0);
                const availableQty = getTotalAvailableQuantity(response.data);

                setProductData({
                    ...response.data,
                    quantity: totalQuantity,
                    originalDbQuantity: availableQty,
                });
            } else {
                showPopup('error', 'Error', 'Failed to fetch product details');
            }
        } catch (error) {
            console.error('Error fetching product details:', error);
            showPopup('error', 'Error', 'Failed to fetch product details');
        } finally {
            setProductLoading(false);
        }
    };

    // FIXED API CALL FUNCTION
    const handleFinish = async () => {
        setSubmitLoading(true);
        try {
            console.log('Starting finish process...');

            // Validate all products
            const validationPromises = productTransactionDataWithQuantity.map(async (product) => {
                try {
                    const productDetails = await getProductDetailsById(product.id);
                    const productName = productDetails.data.product_name || `Product ID ${product.id}`;

                    if (isOutbound) {
                        const productMaxQuantity = getTotalAvailableQuantity(productDetails.data);

                        if (product.quantity > productMaxQuantity) {
                            throw new Error(`You can only outbound up to ${productMaxQuantity} units of ${productName}.`);
                        }

                        if (product.quantity < 1) {
                            throw new Error(`${productName} has invalid quantity (${product.quantity}) for outbound.`);
                        }
                    } else {
                        if (product.quantity < 0) {
                            throw new Error(`${productName} has invalid quantity (${product.quantity}) for inbound.`);
                        }
                    }
                    return { success: true, productId: product.id };
                } catch (error) {
                    return { success: false, error: error.message, productId: product.id };
                }
            });

            const validationResults = await Promise.all(validationPromises);
            const failedValidations = validationResults.filter(result => !result.success);

            if (failedValidations.length > 0) {
                showPopup('error', 'Validation Error', failedValidations[0].error);
                setSubmitLoading(false);
                return;
            }

            let payload;
            const productsPayload = [];

            console.log('Preparing products payload...');

            // Process each product with SKU handling
            // FIXED: Use only unique products from productTransactionDataWithQuantity
            const uniqueProducts = [];
            const processedIds = new Set();

            for (const product of productTransactionDataWithQuantity) {
                if (processedIds.has(product.id)) {
                    // Skip duplicate product - already processed
                    continue;
                }
                processedIds.add(product.id);
                uniqueProducts.push(product);
            }

            for (const product of uniqueProducts) {
                if (product.id && product.quantity > 0) {
                    try {
                        const productDetails = await getProductDetailsById(product.id);

                        if (productDetails.status === 200) {
                            const productData = productDetails.data;
                            const skus = productData.lazada_product?.data?.skus || [];

                            // Handle multiple SKUs
                            if (skus.length > 1 && product.skuQuantities) {
                                // Process each SKU quantity
                                Object.entries(product.skuQuantities).forEach(([skuId, quantity]) => {
                                    if (quantity > 0) {
                                        // Find the SKU details
                                        const sku = skus.find(s => s.SkuId?.toString() === skuId.toString());

                                        productsPayload.push({
                                            id: product.id,
                                            ItemId: parseInt(productData.lazada_item_id || product.id),
                                            SkuId: parseInt(skuId),
                                            quantity: quantity
                                        });
                                    }
                                });
                            } else if (skus.length > 0) {
                                // Single SKU or no SKU quantities specified
                                const sku = skus[0];
                                productsPayload.push({
                                    id: product.id,
                                    ItemId: parseInt(productData.lazada_item_id || product.id),
                                    SkuId: parseInt(sku?.SkuId || product.id),
                                    quantity: product.quantity
                                });
                            } else {
                                // No SKU information
                                productsPayload.push({
                                    id: product.id,
                                    ItemId: parseInt(productData.lazada_item_id || product.id),
                                    SkuId: parseInt(product.id),
                                    quantity: product.quantity
                                });
                            }
                        }
                    } catch (error) {
                        console.error(`Error processing product ${product.id}:`, error);
                        // Fallback
                        productsPayload.push({
                            id: product.id,
                            ItemId: parseInt(product.id),
                            SkuId: parseInt(product.id),
                            quantity: product.quantity
                        });
                    }
                }
            }

            console.log('Final products payload:', productsPayload);

            if (productsPayload.length === 0) {
                showPopup('error', 'Missing Data', 'No valid products selected.');
                setSubmitLoading(false);
                return;
            }

            // Check if multiple SKUs are involved
            const productIds = productsPayload.map(p => p.id);
            const uniqueProductIds = [...new Set(productIds)];
            const isMultipleSku = productsPayload.length > uniqueProductIds.length;

            // FIXED: Show correct product count in success message
            const productCount = uniqueProductIds.length;

            if (isOutbound) {
                payload = {
                    status: "outbound",
                    is_multiple_sku: isMultipleSku,
                    products: productsPayload
                };
            } else {
                payload = {
                    transaction_id: transactionId,
                    status: "inbound",
                    is_multiple_sku: isMultipleSku,
                    products: productsPayload
                };
            }

            console.log('Final API Payload:', JSON.stringify(payload, null, 2));

            const response = await updateQuantityAPI(payload);
            console.log('API Response:', response);

            if (response.status === 200) {
                showPopup('success', 'Success',
                    `${productCount} product(s) ${isOutbound ? 'outbound' : 'inbound'} successfully`);

                // Clear the state
                setProductTransactionDataWithQuantity([]);
                setProductsData([]);
                setSkuQuantities({});

                setTimeout(() => {
                    navigate("/products");
                }, 1500);
            } else {
                throw new Error(response.message || `API returned status ${response.status}`);
            }

        } catch (error) {
            console.error('API Error Details:', error);

            let errorMessage = `Failed to ${isOutbound ? 'outbound' : 'update'} products`;
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            showPopup('error', 'API Error', errorMessage);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Keep your existing useEffect hooks for data fetching
    useEffect(() => {
        const fetchProduct = async () => {
            if (isOutbound || location.state?.productData || (isOutbound && location.state?.barcode)) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const res = await productTransaction(transactionId);
                const sortedData = [...res.data].reverse();
                setProductsData(sortedData);

                const originalQtys = {};
                sortedData.forEach(item => {
                    originalQtys[item.id] = getTotalAvailableQuantity(item);
                });
                setOriginalQuantities(originalQtys);

                setProductTransactionDataWithQuantity(sortedData.map(item => ({
                    id: item.id,
                    quantity: item.quantity
                })));

                const existingTransaction = transactions.find(tx => tx.id === transactionId);
                const existingProducts = existingTransaction ? existingTransaction.products : [];
                const existingProductIds = existingProducts.map(p => p.id);

                const newProducts = sortedData.filter(item => !existingProductIds.includes(item.id))
                    .map(item => ({
                        id: item.id,
                        quantity: item.quantity
                    }));

                if (newProducts.length) {
                    dispatch(setProductsForTransaction({
                        transactionId: transactionId,
                        products: [...existingProducts, ...newProducts]
                    }));
                }

                setProductId(sortedData[0].id);
            } catch (err) {
                setError("Something went wrong while fetching product details.");
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        if (transactionId && transactionId !== "new" && !isOutbound) {
            fetchProduct();
        } else {
            setLoading(false);
        }
    }, [transactionId, isOutbound, location.state, dispatch]);

    useEffect(() => {
        const fetchProductDetails = async () => {
            if (!isOutbound && productTransactionDataWithQuantity && productTransactionDataWithQuantity[0]?.id) {
                try {
                    setProductLoading(true);
                    const response = await getProductDetailsById(productTransactionDataWithQuantity[0].id);
                    if (response.status === 200) {
                        setProductData(response.data);
                    } else {
                        showPopup('error', 'Error', 'Failed to fetch product details');
                    }
                } catch (error) {
                    showPopup('error', 'Error', 'Something went wrong while fetching product details');
                    console.error("Fetch error:", error);
                } finally {
                    setProductLoading(false);
                    setContentReady(true);
                }
            }
        };

        if (!location.state?.productData && productTransactionDataWithQuantity.length > 0) {
            fetchProductDetails();
        }
    }, [isOutbound, location.state, showPopup]);

    useEffect(() => {
        productId && getProductDetails(productId);
    }, [productId]);

    const isPageLoading = loading || !contentReady;

    return (
        <div className={`${Styles.productFeature}`}>
            <PopUpMessage />
            <div className={`${Styles.productFeature_heading}`}>
                <h2>Product Details {isOutbound ? '(Outbound)' : ''}</h2>
                <button
                    className={`${Styles.back_btn}`}
                    onClick={() => navigate("/scanbarcode")}
                >
                    Back
                </button>
            </div>

            {isPageLoading ? (
                <div className={`${Styles.loader_container}`}>
                    <Loader size="large" color="black" />
                </div>
            ) : (
                <div className={`${Styles.product_container}`}>
                    <ProductDetailCard
                        productData={productData}
                        productLoading={productLoading}
                        isOutbound={isOutbound}
                        maxQuantity={maxQuantity}
                        selectedSku={selectedSku}
                        onSkuChange={handleSkuChange}
                        skuQuantities={skuQuantities}
                    />

                    <div className={`${Styles.product_lot}`}>
                        <QuantityManager
                            productData={productData}
                            productTransactionDataWithQuantity={productTransactionDataWithQuantity}
                            productLoading={productLoading}
                            submitLoading={submitLoading}
                            handleIncrease={handleIncrease}
                            handleDecrease={handleDecrease}
                            handleAddMore={handleAddMore}
                            handleFinish={handleFinish}
                            originalQuantities={originalQuantities}
                            isOutbound={isOutbound}
                            isNewlyCreated={newlyCreatedProducts.has(productData?.id)}
                            setProductTransactionDataWithQuantity={setProductTransactionDataWithQuantity}
                            setProductsData={setProductsData}
                            showPopup={showPopup}
                            selectedSku={selectedSku}
                            skuQuantities={skuQuantities}
                            onSkuQuantityChange={handleSkuQuantityChange}
                            getCurrentSkuQuantity={getCurrentSkuQuantity}
                            getAvailableQuantityForSku={() => getAvailableQuantityForSku(selectedSku)}
                        />

                        <TransactionTable
                            isOutbound={isOutbound}
                            productData={productData}
                            transactionId={transactionId}
                            loading={loading}
                            productsData={productsData}
                            setProductId={setProductId}
                            productTransactionDataWithQuantity={productTransactionDataWithQuantity}
                            skuQuantities={skuQuantities}
                            onProductSelect={handleProductSelect}
                        />
                    </div>
                </div>
            )}
            {error && <div className={`${Styles.error_message}`}>{error}</div>}
        </div>
    );
};

export default ProductDetail;